import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { location } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import {
  DETECTIONS_FRAMING_SENTENCE,
  EXCEPTION_INTEGRITY_SENTENCE,
  EXCEPTION_KIND_LABELS,
  EXCEPTION_KIND_MEANINGS,
  EXCEPTION_STATUS_LABELS,
  MISSING_EVIDENCE_EXPLAINER,
  MISSING_EVIDENCE_HEADING,
  MISSING_EVIDENCE_STATE_LABELS,
  PERMISSION_STREAK_EXPLAINER,
  PERMISSION_STREAK_HEADING,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { messageOf } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Exceptions — the review queue behind the clock rules (design doc §8; L117),
 * PLUS the §9 issues surface (L118), folded in rather than given a sub-nav
 * section of its own. The fold is a decision, documented here: the design
 * doc's decided architecture #4 says the exception table IS the V1 issue
 * feed, its hub navigation (#1) lists Exceptions and no separate Issues, and
 * the two derivable detections are the same class of fact read by the same
 * people with the same key — a second section would be the same page twice
 * with a different heading. The detections render as a second card beneath
 * the queue: read-only observations with no lifecycle, no decisions, and no
 * language about intent.
 *
 * Every row is a record the clock path wrote BESIDE a punch it could not
 * verify: the worked time has already landed, and nothing here changes it.
 * Kinds are objective device/policy states, statuses are lifecycle words, and
 * the actions are the two decisions the API offers — approve or return — each
 * stamped with the reviewer and the instant, and refused by the server for
 * one's own record (separation of duties).
 *
 * Reading needs `time:read` (the route's own key); the server scopes the list
 * to the caller's own worker plus their supervision edge unless they hold the
 * org-wide `timekeeping:read_all`. Deciding needs `timekeeping:manage`, so the
 * decision controls render only for a holder — and the server checks again.
 */

type StatusFilter = 'all' | location.LocationExceptionStatus;

const FILTERS: readonly { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending_approval', label: EXCEPTION_STATUS_LABELS['pending_approval']! },
  { value: 'recorded', label: EXCEPTION_STATUS_LABELS['recorded']! },
  { value: 'approved', label: EXCEPTION_STATUS_LABELS['approved']! },
  { value: 'returned', label: EXCEPTION_STATUS_LABELS['returned']! },
];

const STATUS_TONE: Record<string, 'plain' | 'look' | 'unsure' | 'good'> = {
  recorded: 'plain',
  pending_approval: 'look',
  approved: 'good',
  returned: 'unsure',
};

/**
 * Is THIS button's decision the one currently in flight?
 *
 * `variables` is the argument react-query dispatched, so it answers both halves
 * of the question the two booleans on their own cannot: which exception, and
 * which of its two decisions. Written as a function rather than inline twice
 * because the inline form is where the id check gets dropped by the next
 * author and the page silently returns to labelling every row.
 */
function isDeciding(
  decide: { isPending: boolean; variables?: { id: string; decision: string } | undefined },
  id: string,
  decision: 'approve' | 'return',
): boolean {
  return decide.isPending && decide.variables?.id === id && decide.variables.decision === decision;
}

function formatInstant(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function LocationExceptionsPage() {
  const navigate = useNav();
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canDecide = has('timekeeping:manage');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [note, setNote] = useState<Record<string, string>>({});
  /**
   * WHICH exception's decision failed, and what the server said.
   *
   * Keyed by id. It used to be a bare string rendered once above the whole
   * list, so a reviewer working through six exceptions saw one sentence at the
   * top of the card naming none of them — and the next row they scrolled to
   * carried a message about a different row entirely.
   */
  const [decideError, setDecideError] = useState<{ id: string; message: string } | null>(null);

  const query = useQuery({
    queryKey: ['location', 'exceptions', filter],
    queryFn: () => location.listExceptions(filter === 'all' ? {} : { status: filter as never }),
  });

  // The derivable detections beside the queue (§9). Same key, same server-side
  // visibility as the exception list; the server states the windows it applied.
  const issues = useQuery({
    queryKey: ['location', 'issues'],
    queryFn: () => location.listIssues(),
  });

  const decide = useMutation({
    mutationFn: (input: { id: string; decision: 'approve' | 'return' }) => {
      const text = note[input.id]?.trim();
      return input.decision === 'approve'
        ? location.approveException(input.id, text === '' ? undefined : text)
        : location.returnException(input.id, text === '' ? undefined : text);
    },
    onMutate: () => {
      setDecideError(null);
    },
    onSuccess: () => {
      setDecideError(null);
      void qc.invalidateQueries({ queryKey: ['location', 'exceptions'] });
    },
    onError: (err: unknown, input: { id: string; decision: 'approve' | 'return' }) =>
      setDecideError({ id: input.id, message: messageOf(err) }),
  });

  return (
    <LocationSettingsShell active="exceptions">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Location exceptions</h2>
          <label className="cmp-filter">
            <span className="rn-muted">Show</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as StatusFilter)}
              aria-label="Filter exceptions"
            >
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {EXCEPTION_INTEGRITY_SENTENCE}
        </p>

        {query.isLoading ? (
          <SkeletonSet surface="table" rows={4} />
        ) : query.isError && !query.data ? (
          <LoadFailure
            error={query.error}
            message="We couldn’t load the exceptions just now."
            onRetry={() => void query.refetch()}
          />
        ) : (query.data?.exceptions.length ?? 0) === 0 ? (
          <SuccessEmpty
            heading="No exceptions here"
            message={
              filter === 'all'
                ? 'When a punch can’t be verified at a work site, its exception appears here beside the recorded time.'
                : 'No exceptions match this filter.'
            }
          />
        ) : (
          <>
            {query.data!.scope === 'workers' ? (
              <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-6)' }}>
                You’re seeing your own record and the workers assigned to you.
              </p>
            ) : null}
            <ul className="cmp-controls" style={{ marginTop: 'var(--space-10)' }}>
              {query.data!.exceptions.map((ex) => {
                const open = ex.status === 'recorded' || ex.status === 'pending_approval';
                return (
                  <li key={ex.id} className="cmp-control">
                    <div className="cmp-control-head">
                      <span className="cmp-control-title">
                        {EXCEPTION_KIND_LABELS[ex.kind] ?? ex.kind}
                      </span>
                      <Pill variant={STATUS_TONE[ex.status] ?? 'plain'} dot>
                        {EXCEPTION_STATUS_LABELS[ex.status] ?? ex.status}
                      </Pill>
                    </div>
                    <p className="rn-muted cmp-control-desc">
                      {EXCEPTION_KIND_MEANINGS[ex.kind] ?? ''}
                    </p>
                    <p className="rn-muted" style={{ fontSize: 13 }}>
                      {ex.workerName ?? 'A worker'} ·{' '}
                      {ex.punchKind === 'clock_in' ? 'clock-in' : 'clock-out'} ·{' '}
                      {formatInstant(ex.createdAt)}
                    </p>
                    {ex.attestation ? (
                      <blockquote className="rn-muted" style={{ margin: 'var(--space-6) 0' }}>
                        In the worker’s words: “{ex.attestation}”
                      </blockquote>
                    ) : null}
                    {ex.reviewNote ? (
                      <p className="rn-muted" style={{ fontSize: 13 }}>
                        Reviewer’s note: {ex.reviewNote}
                      </p>
                    ) : null}
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--space-8)',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        marginTop: 'var(--space-6)',
                      }}
                    >
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        onClick={() => navigate(`/sessions/${ex.timeSessionId}`)}
                      >
                        Open the session
                      </button>
                      {canDecide && open ? (
                        <>
                          <input
                            type="text"
                            placeholder="Note to the worker (optional)"
                            aria-label={`Note for this ${EXCEPTION_KIND_LABELS[ex.kind] ?? 'exception'}`}
                            value={note[ex.id] ?? ''}
                            maxLength={2000}
                            onChange={(e) =>
                              setNote((current) => ({ ...current, [ex.id]: e.target.value }))
                            }
                            style={{ flex: '1 1 200px', minWidth: 160 }}
                          />
                          {/* PENDING IS KEYED TO THIS EXCEPTION AND THIS
                              DECISION. One `decide` mutation serves every row
                              on the page, so `decide.isPending` alone disabled
                              Approve and Return on EVERY exception at once and
                              named the one in flight on none of them: a
                              reviewer approving the third of eight watched all
                              sixteen buttons grey out with nothing saying which
                              decision was being recorded, or about whom. */}
                          <button
                            type="button"
                            className="rn-btn"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ id: ex.id, decision: 'approve' })}
                          >
                            {isDeciding(decide, ex.id, 'approve') ? 'Approving…' : 'Approve'}
                          </button>
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ id: ex.id, decision: 'return' })}
                          >
                            {isDeciding(decide, ex.id, 'return') ? 'Returning…' : 'Return'}
                          </button>
                        </>
                      ) : null}
                    </div>
                    {decideError?.id === ex.id ? (
                      <p className="tc-warn" role="alert" style={{ marginTop: 'var(--space-6)' }}>
                        {decideError.message} This exception has not been decided.
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Detections</h2>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {DETECTIONS_FRAMING_SENTENCE}
        </p>

        {issues.isLoading ? (
          <SkeletonSet surface="table" rows={2} />
        ) : issues.isError && !issues.data ? (
          <LoadFailure
            error={issues.error}
            message="We couldn’t load the detections just now."
            onRetry={() => void issues.refetch()}
          />
        ) : (issues.data?.missingEvidence.length ?? 0) === 0 &&
          (issues.data?.permissionStreaks.length ?? 0) === 0 ? (
          <SuccessEmpty
            heading="Nothing detected"
            message={`In the last ${issues.data?.applied.lookbackDays ?? 14} days, every punch that your rules expected evidence for has some, and no worker's recent working days all show location permission off.`}
          />
        ) : (
          <>
            {(issues.data?.missingEvidence.length ?? 0) > 0 ? (
              <div style={{ marginTop: 'var(--space-10)' }}>
                <h3 className="rn-card-title" style={{ fontSize: '0.9rem' }}>
                  {MISSING_EVIDENCE_HEADING}
                </h3>
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  {MISSING_EVIDENCE_EXPLAINER}
                </p>
                <ul className="cmp-controls" style={{ marginTop: 'var(--space-6)' }}>
                  {issues.data!.missingEvidence.map((p) => (
                    <li key={p.punchId} className="cmp-control">
                      <div className="cmp-control-head">
                        <span className="cmp-control-title">
                          {MISSING_EVIDENCE_STATE_LABELS[p.locationVerification] ??
                            p.locationVerification}
                        </span>
                      </div>
                      <p className="rn-muted" style={{ fontSize: 13 }}>
                        {p.workerName || 'A worker'} ·{' '}
                        {p.punchKind === 'clock_in' ? 'clock-in' : 'clock-out'} ·{' '}
                        {formatInstant(p.occurredAt)} · required by the{' '}
                        {p.policySource === 'program' ? 'program' : 'organization'} policy
                      </p>
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        onClick={() => navigate(`/sessions/${p.timeSessionId}`)}
                      >
                        Open the session
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {(issues.data?.permissionStreaks.length ?? 0) > 0 ? (
              <div style={{ marginTop: 'var(--space-10)' }}>
                <h3 className="rn-card-title" style={{ fontSize: '0.9rem' }}>
                  {PERMISSION_STREAK_HEADING}
                </h3>
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  {PERMISSION_STREAK_EXPLAINER}
                </p>
                <ul className="cmp-controls" style={{ marginTop: 'var(--space-6)' }}>
                  {issues.data!.permissionStreaks.map((s) => (
                    <li key={s.workerId} className="cmp-control">
                      <div className="cmp-control-head">
                        <span className="cmp-control-title">
                          {s.workerName || 'A worker'} — {s.streakDays} working{' '}
                          {s.streakDays === 1 ? 'day' : 'days'}
                        </span>
                      </div>
                      <p className="rn-muted" style={{ fontSize: 13 }}>
                        Every day they punched from {s.firstDay} through {s.lastDay} recorded
                        location permission as disabled.
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </section>
    </LocationSettingsShell>
  );
}
